/**
 * TIP Shell — UI state store (framework-agnostic; the Preact App subscribes to it).
 * The security core (bridge/tripwire) mutates the UI ONLY through `actions`, so the
 * view layer can be swapped without touching the seal.
 */
import type { Tier } from './protocol.js';

export type LogKind = 'out' | 'in' | 'warn' | 'info' | 'breach';

export interface LogEntry {
  id: number;
  t: string; // HH:MM:SS.mmm
  kind: LogKind;
  msg: string;
}
/** Hard cap on retained painted slots (defense in depth behind the Bridge's own cap). */
const MAX_SLOTS = 8;

/** Most chips the strip ever shows: the viewer's own (always first, always reserved) plus
 *  classmates sharing what is left. One chip per TAG. */
export const MAX_STRIP_CHIPS = 3;

/**
 * Which chips the trusted strip shows, in order. The viewer's OWN name always comes first
 * and always has its place — an app used to be able to fill all three places with
 * classmates ("Jordan · Al · Kim" beside "Sealed", no viewer name at all, and the overlay
 * is aria-hidden so a screen reader got no name either). Pure, so it is unit-tested.
 */
export function stripChips(slots: SlotEntry[], selfTag: string | null): SlotEntry[] {
  const own = selfTag ? slots.find((s) => s.tag === selfTag) : undefined;
  const out: SlotEntry[] = own ? [own] : [];
  const seen = new Set<string>(own ? [own.tag] : []);
  const others = MAX_STRIP_CHIPS - 1; // the viewer's place is reserved even when empty
  let n = 0;
  for (const s of slots) {
    if (n >= others) break;
    if (seen.has(s.tag) || s.tag === selfTag) continue;
    seen.add(s.tag);
    out.push(s);
    n += 1;
  }
  return out;
}

export interface SlotEntry {
  slotId: string;
  tag: string;
  name: string;
}
/** A painted leaderboard row (trusted chrome). `name` is null when tags-only. */
export interface LeaderEntry {
  tag: string;
  name: string | null;
  score: number;
  maxScore: number;
}
export interface LeaderboardState {
  slotId: string;
  rows: LeaderEntry[];
}
export interface BreachState {
  reason: string;
  killed: boolean; // true once /admin/kill is confirmed
}

export interface ShellState {
  status: string;
  tier: Tier | null;
  appId: string | null;
  denied: boolean;
  denyReason: string | null;
  breach: BreachState | null;
  slots: SlotEntry[];
  leaderboard: LeaderboardState | null; // trusted-chrome scope-ref leaderboard (parent DOM)
  log: LogEntry[];
  inputForwarding: boolean; // standard = true; high_assurance = false
  /** True only when the computed grant hands the frame first names (standard tier AND
   *  first_name exposed). Drives the seal note, so it never claims more than is true. */
  appSeesFirstNames: boolean;
  needLogin: boolean; // no first-party session ⇒ show the login gate (fail-closed)
  loginUrl: string | null; // ${API_ORIGIN}/auth/login — the top-level nav target
  loginMessage: string | null; // fail-closed detail (e.g. ?login=denied from the callback)
  loginTone: 'warn' | 'info'; // a denial warns; "you signed out" is just information
  /** Session role for PRESENTATION decisions only (diagnostics visibility) —
   *  never an authz boundary; every endpoint re-checks server-side. */
  role: string | null;
  /** The viewer's own opaque tag, so the strip can pin the viewer's chip first. */
  selfTag: string | null;
}

/** Imperative hook from the view into the Bridge (wired by the bootstrap). */
export interface ShellController {
  onInput(value: string): void;
}
export const controller: ShellController = { onInput: () => {} };

type Listener = () => void;

let state: ShellState = {
  status: 'initializing…',
  tier: null,
  appId: null,
  denied: false,
  denyReason: null,
  breach: null,
  slots: [],
  leaderboard: null,
  log: [],
  inputForwarding: false,
  appSeesFirstNames: false,
  needLogin: false,
  loginUrl: null,
  loginMessage: null,
  loginTone: 'warn',
  role: null,
  selfTag: null,
};

const listeners = new Set<Listener>();
let logSeq = 0;

export function getState(): ShellState {
  return state;
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function setState(patch: Partial<ShellState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export const actions = {
  setStatus(status: string): void {
    setState({ status });
  },
  setTier(tier: Tier): void {
    setState({ tier });
  },
  setApp(appId: string): void {
    setState({ appId });
  },
  setAppSeesFirstNames(appSeesFirstNames: boolean): void {
    setState({ appSeesFirstNames });
  },
  setInputForwarding(inputForwarding: boolean): void {
    setState({ inputForwarding });
  },
  setRole(role: string): void {
    setState({ role });
  },
  deny(reason: string): void {
    setState({ denied: true, denyReason: reason, status: 'DENIED at mount (fail-closed)' });
  },
  /** No valid session ⇒ show the "Sign in with Google" gate; no gate call, no frame. */
  needLogin(loginUrl: string, message?: string, tone: 'warn' | 'info' = 'warn'): void {
    setState({
      needLogin: true,
      loginUrl,
      loginMessage: message ?? null,
      loginTone: tone,
      status: 'sign-in required (fail-closed — no session, no frame)',
    });
  },
  breach(b: BreachState): void {
    setState({ breach: b, status: b.killed ? 'BREACH — quarantined' : 'BREACH — quarantine in progress' });
  },
  setSelfTag(selfTag: string): void {
    setState({ selfTag });
  },
  /** Bounded twice over: the Bridge binds at most MAX_NAME_SLOTS slots per frame, and
   *  this keeps at most MAX_SLOTS entries regardless. Eviction takes CLASSMATE entries
   *  first: an app must never be able to push the viewer's own name out of the trusted
   *  strip by painting other names after it (review round 2). */
  paintSlot(slotId: string, tag: string, name: string): void {
    const slots = state.slots.filter((s) => s.slotId !== slotId);
    slots.push({ slotId, tag, name });
    while (slots.length > MAX_SLOTS) {
      const i = slots.findIndex((s) => s.tag !== state.selfTag);
      slots.splice(i >= 0 ? i : 0, 1);
    }
    setState({ slots });
  },
  /** Paint the trusted-chrome leaderboard (parent DOM). Rows already carry resolved
   *  names (or null) + scores; the view renders them, and they never re-enter the frame. */
  paintLeaderboard(slotId: string, rows: LeaderEntry[]): void {
    setState({ leaderboard: { slotId, rows } });
  },
  log(kind: LogKind, msg: string): void {
    const entry: LogEntry = { id: ++logSeq, t: new Date().toISOString().slice(11, 23), kind, msg };
    setState({ log: state.log.concat(entry).slice(-300) });
  },
};
